import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Archive, Download, GitMerge, Plus, Search, Trash2 } from 'lucide-react';
import {
  createCustomer,
  listCustomerIds,
  listCustomers,
  unarchiveCustomer,
  type CustomerListItem,
} from '@/lib/api/customers.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { CustomerStatusBadge } from '@/components/common/CustomerStatusBadge.js';
import { EmptyState } from '@/components/common/EmptyState.js';
import { TableRowSkeleton } from '@/components/common/LoadingSkeleton.js';
import { Pagination } from '@/components/common/Pagination.js';
import { SlideOver } from '@/components/SlideOver.js';
import {
  ArchiveCustomerModal,
  BulkArchiveModal,
  BulkDeleteModal,
  DeleteCustomerModal,
} from '@/components/customers/CustomerArchiveModals.js';
import { BulkMergeCustomersModal } from '@/components/customers/BulkMergeCustomersModal.js';
import { CustomerForm, type CustomerFormValues } from './CustomerForm.js';
import { formatCurrency, formatDate } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import { isManagerRole } from '@/lib/permissions.js';
import { useAuthStore } from '@/stores/auth.js';
import { cn } from '@/lib/utils.js';

const PAGE_SIZE = 50;

function customerName(c: CustomerListItem): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ');
}

export function CustomerListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role ?? 'read_only');
  const { can } = usePermissions();
  const canViewArchived = can('view_archived_customers');
  const canArchive = isManagerRole(role);
  const canHardDelete = role === 'tenant_admin' || role === 'platform_admin';

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [archiveTarget, setArchiveTarget] = useState<CustomerListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomerListItem | null>(null);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkMergeOpen, setBulkMergeOpen] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, includeArchived]);

  const customersQuery = useQuery({
    queryKey: ['customers', debouncedSearch, page, includeArchived && canViewArchived],
    queryFn: () =>
      listCustomers({
        q: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
        includeArchived: includeArchived && canViewArchived,
      }),
  });

  const meta = customersQuery.data?.meta;
  const total = meta?.total ?? 0;
  const rows = customersQuery.data?.data ?? [];

  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const vipLabel = storeQuery.data?.vipLabel ?? 'VIP';
  const flaggedLabel = storeQuery.data?.flaggedLabel ?? 'Flagged';

  const pageIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const createMutation = useMutation({
    mutationFn: (values: CustomerFormValues) => createCustomer(values),
    onSuccess: (customer) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setCreateOpen(false);
      toastSuccess('Customer created successfully');
      navigate(`/customers/${customer.id as string}`);
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const unarchiveMut = useMutation({
    mutationFn: (id: string) => unarchiveCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toastSuccess('Customer restored');
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectPage() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        for (const id of pageIds) next.delete(id);
      } else {
        for (const id of pageIds) next.add(id);
      }
      return next;
    });
  }

  async function selectAllMatching() {
    try {
      const res = await listCustomerIds({
        q: debouncedSearch,
        includeArchived: includeArchived && canViewArchived,
      });
      setSelected(new Set(res.data));
      toastSuccess(`Selected ${res.data.length} customers`);
    } catch (error) {
      toastError(parseApiError(error).message);
    }
  }

  function exportSelectedCsv() {
    const selectedRows = rows.filter((r) => selected.has(r.id));
    // Include any selected IDs not on this page as id-only rows
    const known = new Set(selectedRows.map((r) => r.id));
    const lines = [
      ['id', 'firstName', 'lastName', 'phone', 'email', 'totalTickets', 'totalSpend', 'archived'].join(
        ',',
      ),
      ...selectedRows.map((r) =>
        [
          r.id,
          JSON.stringify(r.firstName),
          JSON.stringify(r.lastName ?? ''),
          JSON.stringify(r.phone ?? ''),
          JSON.stringify(r.email ?? ''),
          r.totalTickets,
          r.totalSpend,
          r.archivedAt ? 'yes' : 'no',
        ].join(','),
      ),
      ...[...selected]
        .filter((id) => !known.has(id))
        .map((id) => [id, '', '', '', '', '', '', ''].join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['customers'] });
    setSelected(new Set());
  }

  const selectedIds = [...selected];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-xl flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            placeholder="Search by name, phone, or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {canViewArchived ? (
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
              Show archived
            </label>
          ) : null}
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#164477]"
          >
            <Plus className="h-4 w-4" />
            New Customer
          </button>
        </div>
      </div>

      {selected.size > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
          <span className="font-semibold text-slate-900">{selected.size} selected</span>
          <button
            type="button"
            className="text-slate-600 underline"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
          {selected.size < total ? (
            <button type="button" className="text-primary underline" onClick={() => void selectAllMatching()}>
              Select all {total} matching
            </button>
          ) : null}
          <div className="ml-auto flex flex-wrap gap-2">
            {canArchive && selected.size >= 2 ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-800"
                onClick={() => setBulkMergeOpen(true)}
              >
                <GitMerge className="h-4 w-4" />
                Merge
              </button>
            ) : null}
            {canArchive ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-800"
                onClick={() => setBulkArchiveOpen(true)}
              >
                <Archive className="h-4 w-4" />
                Archive
              </button>
            ) : null}
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-800"
              onClick={exportSelectedCsv}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            {canHardDelete ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 font-semibold text-red-800"
                onClick={() => setBulkDeleteOpen(true)}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-3">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={toggleSelectPage}
                  aria-label="Select all on page"
                />
              </th>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Phone</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Total Tickets</th>
              <th className="px-5 py-3">Total Spend</th>
              <th className="px-5 py-3">Last Visit</th>
              <th className="px-5 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((customer) => {
              const archived = Boolean(customer.archivedAt);
              return (
                <tr
                  key={customer.id}
                  className={cn(
                    'cursor-pointer hover:bg-slate-50',
                    archived && 'bg-slate-50/80 text-slate-500',
                  )}
                  onClick={() => navigate(`/customers/${customer.id}`)}
                >
                  <td className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(customer.id)}
                      onChange={() => toggleSelect(customer.id)}
                      aria-label={`Select ${customerName(customer)}`}
                    />
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 font-semibold text-slate-950">
                    <span className="inline-flex items-center gap-2">
                      <span className={archived ? 'text-slate-500' : undefined}>
                        {customerName(customer)}
                      </span>
                      <CustomerStatusBadge
                        status={customer.customerStatus}
                        vipLabel={vipLabel}
                        flaggedLabel={flaggedLabel}
                        reason={customer.statusReason ?? null}
                      />
                      {archived ? (
                        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                          Archived
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-600">{customer.phone ?? '-'}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-600">{customer.email ?? '-'}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-600">{customer.totalTickets}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-600">
                    {formatCurrency(customer.totalSpend)}
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-600">
                    {formatDate(customer.lastVisitAt)}
                  </td>
                  <td
                    className="whitespace-nowrap px-5 py-4 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {archived && canArchive ? (
                      <button
                        type="button"
                        className="text-sm font-semibold text-primary"
                        onClick={() => unarchiveMut.mutate(customer.id)}
                      >
                        Unarchive
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="text-sm font-semibold text-primary"
                        onClick={() => navigate(`/customers/${customer.id}`)}
                      >
                        Open
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {customersQuery.isLoading ? (
              <>
                {[1, 2, 3, 4, 5].map((i) => (
                  <TableRowSkeleton key={i} columns={8} />
                ))}
              </>
            ) : null}
            {!customersQuery.isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState
                    icon="👥"
                    title="No customers found"
                    description="No customers match your search. Try a different name or phone number."
                    action={{ label: 'Add Customer', onClick: () => setCreateOpen(true) }}
                  />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={meta?.pageSize ?? PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <SlideOver title="New Customer" open={createOpen} onClose={() => setCreateOpen(false)}>
        <CustomerForm
          submitLabel="Create customer"
          loading={createMutation.isPending}
          onSubmit={(values) => createMutation.mutate(values)}
        />
      </SlideOver>

      <ArchiveCustomerModal
        open={Boolean(archiveTarget)}
        customer={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onDone={invalidate}
      />
      <DeleteCustomerModal
        open={Boolean(deleteTarget)}
        customer={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDone={invalidate}
        onArchiveInstead={() => {
          if (deleteTarget) setArchiveTarget(deleteTarget);
        }}
      />
      <BulkArchiveModal
        open={bulkArchiveOpen}
        customerIds={selectedIds}
        onClose={() => setBulkArchiveOpen(false)}
        onDone={invalidate}
      />
      <BulkDeleteModal
        open={bulkDeleteOpen}
        customerIds={selectedIds}
        onClose={() => setBulkDeleteOpen(false)}
        onDone={invalidate}
      />
      <BulkMergeCustomersModal
        open={bulkMergeOpen}
        customerIds={selectedIds}
        onClose={() => setBulkMergeOpen(false)}
        onDone={(primaryId) => {
          invalidate();
          navigate(`/customers/${primaryId}`);
        }}
      />
    </div>
  );
}
